import type { SxProps, Theme } from "@mui/material";
import { Autocomplete, Stack, TextField } from "@mui/material";
import type { CustomerAccountManager } from "@przezmorze/api-types";
import { uniqBy } from "lodash";
import type { FC, ReactNode } from "react";
import { useCustomers } from "../../customers/useCustomers";

interface Props {
  readonly accountManagerId: string | null;
  readonly onChange: (accountManagerId: string | null, customerIds: string[] | null) => void;
  readonly size?: "small" | "medium";
  readonly disabled?: boolean;
  readonly required?: boolean;
  readonly disableClearable?: boolean;
  readonly showLink?: boolean;
  readonly helperText?: ReactNode;
  readonly sx?: SxProps<Theme>;
}

/** Customer picker that narrows the options to customers whose name contains the typed text. */
const AccountManagerSelect: FC<Props> = ({
  accountManagerId,
  onChange,
  size,
  disabled,
  required,
  disableClearable,
  showLink,
  helperText,
  sx,
}) => {
  const { customers } = useCustomers();
  const accountManagers = uniqBy(
    customers.map((c) => c.accountManager).filter((am) => am),
    (am) => am?.id,
  ) as CustomerAccountManager[];
  const selectedAccountManager = accountManagers.find((am) => am.id === accountManagerId) ?? null;

  const customersOfAccountManager = (accountManager?: string) => {
    if (!accountManager) {
      return null;
    }
    return customers.filter((c) => c.accountManager?.id == accountManager).map((c) => c.id);
  };

  const autocomplete = (
    <Autocomplete
      options={accountManagers}
      value={selectedAccountManager}
      getOptionKey={(am) => am.id}
      getOptionLabel={(am) => am.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      fullWidth
      size={size}
      disabled={disabled}
      disableClearable={disableClearable}
      sx={sx}
      onChange={(_, accountManager) =>
        onChange(accountManager?.id ?? null, customersOfAccountManager(accountManager?.id))
      }
      renderInput={(params) => <TextField {...params} label="Opiekun" required={required} helperText={helperText} />}
    />
  );
  if (!showLink) {
    return autocomplete;
  }
  return (
    <Stack direction="row" sx={{ width: "100%", height: "fit-content" }}>
      {autocomplete}
    </Stack>
  );
};

export default AccountManagerSelect;
